import { createRouter, RouterProvider } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Provider } from "jotai";
import { getDefaultStore } from "jotai";
import { apiClient, ApiProvider } from "@tubo/api-models/context";
import { setupAuthInterceptors } from "@tubo/api-models/context/api-client";
import { clearAuthAtom, authTokenAtom, activeTenantIdAtom, setAuthAtom } from "@tubo/store";
import { recordNavMemory } from "@client/atoms/nav-memory";
import { DefaultPendingFrame } from "@client/components/nav-pending-frame";
import { WebSocketProvider } from "./contexts/WebSocketContext";
import { routeTree } from "./routeTree.gen";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 90_000, // 90 秒内数据视为新鲜，避免重复请求
      // 全局禁用「窗口焦点切换 → 自动 refetch」机制。
      //
      // 焦点切换偷偷 refetch 会破坏用户编辑态——已导致
      //   /settings/ai-models 添加 provider 弹窗在用户编辑期间莫名复位。
      //   及一些当时没有定位到原因的奇怪的数据清除/复位
      // 所有 mutation 必须显式 invalidateQueries 触发关联 query 刷新。
      refetchOnWindowFocus: false,
    },
  },
});

// --- 认证拦截器 ---

setupAuthInterceptors({
  getToken: () => getDefaultStore().get(authTokenAtom),
  setToken: (token: string) => {
    getDefaultStore().set(authTokenAtom, token);
  },
  clearAuth: () => {
    getDefaultStore().set(clearAuthAtom);
  },
  getActiveTenantId: () => getDefaultStore().get(activeTenantIdAtom),
  onAuthExpired: () => {
    const redirect = encodeURIComponent(
      window.location.href.replace(window.location.origin, ""),
    );
    window.location.href = `/login?redirect=${redirect}`;
  },
});

// e2e 专用（仅 dev 构建）：直接注入 JWT，绕过登录页（对齐 ppt __pptController 先例）
declare global {
  interface Window {
    __e2eSetAuth?: (token: string, tenantId?: string | null) => void;
    /** e2e/排查专用：读取 React Query 缓存状态（切换租户缓存清空断言用） */
    __queryClient?: QueryClient;
    /** e2e 专用：playwright evaluateOnNewDocument 预置的令牌，应用模块求值时消费 */
    __e2ePendingAuth?: { token: string; tenantId?: string | null };
  }
}
if (import.meta.env.DEV) {
  window.__e2eSetAuth = (token, tenantId = null) => {
    getDefaultStore().set(setAuthAtom, { token });
    getDefaultStore().set(activeTenantIdAtom, tenantId);
  };
  // e2e：evaluateOnNewDocument 预置的令牌在应用模块求值时消费（路由守卫之前），
  // 使带守卫的真实路由（如 /file-preview）可直接以注入态打开。
  const pendingAuth = window.__e2ePendingAuth;
  if (pendingAuth?.token) {
    getDefaultStore().set(setAuthAtom, { token: pendingAuth.token });
    getDefaultStore().set(activeTenantIdAtom, pendingAuth.tenantId ?? null);
  }
  window.__queryClient = queryClient;
}

const router = createRouter({
  routeTree,
  context: { api: apiClient, queryClient },
  basepath: import.meta.env.VITE_BASE_PATH || "/",
  // 顶级页面切换即时上屏静态框架：路由组件 chunk 未就绪时，pendingMs 到点
  // （0=立即）提交新路由树，组件挂起落入 match 级 Suspense，显示
  // nav-pending-frame 注册的骨架，chunk 到位后原地接管（详见该文件头注释）。
  // 各入口路由组件均为 autoCodeSplitting 拆分的懒组件（带 .preload，
  // routeNeedsPreload 命中），pendingTimeout 会武装，无需逐路由配置。
  // pendingMinMs 同样归零（默认 500ms）：骨架只显示 chunk 实际加载窗口，
  // 不给页面接管人为加最短时长。
  defaultPendingComponent: DefaultPendingFrame,
  defaultPendingMs: 0,
  defaultPendingMinMs: 0,
});

// 全局记录每次导航的完整 URL，按功能首段路径作为 key。
// onResolved 在导航完成（含路由匹配、loader、重定向）后触发，
// 确保记录的是最终 URL（如 /data-center → 重定向 → /data-center/exports 记录的是后者）。
router.subscribe("onResolved", ({ toLocation, hrefChanged }) => {
  if (!hrefChanged) return;
  recordNavMemory(toLocation.pathname, toLocation.href);
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export function App() {
  return (
    <Provider store={getDefaultStore()}>
      <ApiProvider axiosInstance={apiClient}>
        <QueryClientProvider client={queryClient}>
          <WebSocketProvider>
            <RouterProvider router={router} />
          </WebSocketProvider>
        </QueryClientProvider>
      </ApiProvider>
    </Provider>
  );
}
